<script lang="ts">
	import { mutation } from "@api/graphql.js";
	import sendWebMessage from "@api/mutations/sendWebMessage.graphql";
	import Button from "@components/common/Button.svelte";
	import Link from "@components/common/Link.svelte";
	import Modal from "@components/common/Modal.svelte";
	import { ButtonVariant } from "@components/common/getButtonClass.js";
	import { LinkVariant } from "@components/common/getLinkClass.js";
	import Form from "@components/form/Form.svelte";
	import LabelledFileUploader from "@components/form/LabelledFileUploader.svelte";
	import LabelledInput from "@components/form/LabelledInput.svelte";
	import LabelledSelect from "@components/form/LabelledSelect.svelte";
	import type { HTMLFormAttributes } from "svelte/elements";
	import type { UploadedFile } from "@core/schema/UploadedFile.js";
	import { UploadedFileBucket } from "@core/schema/UploadedFileBucket.js";
	import type { WebMessageInput } from "@core/schema/WebMessage.js";
	import { WebMessageCareerPosition } from "@core/schema/WebMessageCareerPosition.js";
	import { WebMessageType } from "@core/schema/WebMessageType.js";
	import { webMessageCareerPositionNameMap } from "@core/schema/names/webMessageCareerPositionNameMap.js";
	import { getPrivacyPolicyPath } from "@core/schema/paths/getPrivacyPolicyPath.js";
	import type { Complete } from "@core/utils/Complete.js";
	import type { Option } from "@core/utils/Option.js";
	import { isEnum } from "@core/utils/enum/isEnum.js";
	import { createPromiseWithStatus } from "@core/utils/svelte/createPromiseWithStatus.svelte.js";
	import { getGlobalSettings } from "../contexts/getGlobalSettings.js";
	import { getGlobalTemplateProps } from "../contexts/getGlobalTemplateProps.js";
	import { type CareerPosition, careerPositionsByLocale } from "../data/careerPositions.js";

	const { position, ...rest }: Omit<HTMLFormAttributes, "onsubmit"> & { position?: CareerPosition } = $props();
	const { PUBLIC_GRAPHQL_ENDPOINT } = getGlobalSettings();
	const { tenant } = getGlobalTemplateProps();

	let showSuccessModal = $state<boolean>(false);
	let errorHappened = $state<boolean>(false);
	let gdprApprovedAt = $state<string | null>(null);
	let attachment = $state<UploadedFile | undefined>(undefined);

	function onGdprToggle(event: Event): void {
		const input = event.target as HTMLInputElement;
		gdprApprovedAt = input.checked ? new Date().toISOString() : null;
	}

	const send = createPromiseWithStatus(async (input: Omit<WebMessageInput, "contactDetail">) =>
		mutation<boolean, { input: typeof input }>(PUBLIC_GRAPHQL_ENDPOINT, sendWebMessage, { input }),
	);

	const isWebMessageCareerPosition = isEnum(WebMessageCareerPosition);

	async function submit(event: SubmitEvent): Promise<void> {
		event.preventDefault();
		if (!gdprApprovedAt) {
			throw new Error("GDPR must be approved.");
		}

		errorHappened = false;

		const form = event.target as HTMLFormElement;
		const formData = new FormData(form);
		const name = formData.get("name") as string;
		const phoneNumber = formData.get("phoneNumber") as string;
		const email = formData.get("email") as string;
		const linkedInUrl = (formData.get("linkedInUrl") as string | null) ?? undefined;
		const position = (formData.get("position") as string | null) ?? undefined;
		if (!isWebMessageCareerPosition(position)) {
			throw new Error("Invalid position value");
		}

		try {
			const input: Complete<Omit<WebMessageInput, "contactDetail">> = {
				name,
				phoneNumber,
				email,
				type: WebMessageType.Career,
				gdprApprovedAt,
				careerDetail: {
					attachment: attachment && {
						...attachment,
						title: attachment.title ?? "Nahraný soubor",
					},
					linkedInUrl: linkedInUrl ? linkedInUrl : undefined,
					position,
				},
				locale: tenant.locale,
			};
			await send.call(input);
			showSuccessModal = true;
			form.reset();
			// eslint-disable-next-line require-atomic-updates
			attachment = undefined;
		} catch (error) {
			// eslint-disable-next-line no-console
			console.error(error);
			errorHappened = true;
		}
	}

	const availablePositionOptions = $derived.by<Option[]>(() => {
		const availablePositions = careerPositionsByLocale[tenant.locale].map(({ position }: CareerPosition) => position);
		return availablePositions.map((position: WebMessageCareerPosition) => ({
			value: position,
			label: webMessageCareerPositionNameMap[position][tenant.locale],
		}));
	});
</script>

<Modal show={showSuccessModal}>
	<p class="text-xl text-fstonyx">Děkujeme za zájem</p>
	<div class="text-center flex flex-col gap-8">
		<p>
			Formulář byl úspěšně odeslán.
			<br />
			Brzy se vám ozveme.
		</p>

		<Button
			variant={ButtonVariant.Phlox}
			class="ml-auto"
			onclick={(): void => {
				showSuccessModal = false;
			}}
		>
			Rozumím
		</Button>
	</div>
</Modal>

<div class="flex flex-col items-center gap-8">
	<Form {...rest} onsubmit={submit} class={["grid grid-cols-1 lg:grid-cols-2 gap-4", rest.class]}>
		<LabelledInput name="name" required>Jméno a přijmení</LabelledInput>
		<LabelledInput type="email" name="email" required>E-mail</LabelledInput>
		<LabelledInput name="phoneNumber" type="tel" required>Telefonní číslo</LabelledInput>
		<LabelledSelect name="position" required>
			Pozice

			{#snippet options()}
				<option value="" disabled selected>-- Vyberte --</option>
				{#each availablePositionOptions as { value, label } (value)}
					<option {value} selected={position?.position === (value as WebMessageCareerPosition)}>{label}</option>
				{/each}
			{/snippet}
		</LabelledSelect>

		<LabelledFileUploader
			wrapperClass="col-span-full"
			destinationFolder="careerFormAttachment"
			bucket={UploadedFileBucket.Upload}
			currentFiles={attachment ? [attachment] : []}
			onchange={(file: UploadedFile | undefined): void => {
				attachment = file;
			}}
		>
			Přilož CV nebo pošli odkaz na tvůj LinkedIn
		</LabelledFileUploader>

		<LabelledInput type="url" name="linkedInUrl" wrapperClass="col-span-full">URL na LinkedIn</LabelledInput>

		<LabelledInput type="checkbox" wrapperClass="col-span-full" onchange={onGdprToggle} required>
			Souhlasím se
			<Link variant={LinkVariant.Underline} href={getPrivacyPolicyPath(tenant)}>
				Zásadami zpracování osobních údajů
			</Link>
		</LabelledInput>

		<Button
			variant={ButtonVariant.Green}
			disabled={send.value.status === "loading"}
			class="place-self-center col-span-full"
		>
			{#if send.value.status === "loading"}
				Odesílání…
			{:else}
				Odeslat
			{/if}
		</Button>
	</Form>

	{#if errorHappened}
		<p class="text-fstphlox">
			Omlouváme se, ale při odesílání formuláře došlo k chybě. Zkuste to prosím znovu nebo později.
		</p>
	{/if}
</div>
